/**
 * Model Data Definitions
 *
 * All available models with metadata, indexed by ModelSpec.
 * Verified working as of 2025-12-17, updated 2025-01-15.
 */

import type { ModelSpec, ModelInfo } from "./types.js";

/**
 * All available models with metadata
 */
export const MODELS: ModelInfo[] = [
  // Anthropic models (verified 2025-12-26)
  {
    spec: "anthropic:claude-opus-4-5-20251101",
    provider: "anthropic",
    model: "claude-opus-4-5-20251101",
    name: "Claude Opus 4.5 (Most Intelligent)",
    contextWindow: 200000,
    speed: "slow",
    cost: "high",
  },
  {
    spec: "anthropic:claude-haiku-4-5-20251001",
    provider: "anthropic",
    model: "claude-haiku-4-5-20251001",
    name: "Claude Haiku 4.5 (Ultra Fast)",
    contextWindow: 200000,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "anthropic:claude-sonnet-4-5-20250929",
    provider: "anthropic",
    model: "claude-sonnet-4-5-20250929",
    name: "Claude Sonnet 4.5 (Best for Coding)",
    contextWindow: 200000,
    speed: "fast",
    cost: "medium",
  },
  {
    spec: "anthropic:claude-opus-4-1-20250805",
    provider: "anthropic",
    model: "claude-opus-4-1-20250805",
    name: "Claude Opus 4.1",
    contextWindow: 200000,
    speed: "slow",
    cost: "high",
  },
  {
    spec: "anthropic:claude-opus-4-20250514",
    provider: "anthropic",
    model: "claude-opus-4-20250514",
    name: "Claude Opus 4",
    contextWindow: 200000,
    speed: "slow",
    cost: "high",
  },
  {
    spec: "anthropic:claude-sonnet-4-20250514",
    provider: "anthropic",
    model: "claude-sonnet-4-20250514",
    name: "Claude Sonnet 4",
    contextWindow: 200000,
    speed: "fast",
    cost: "medium",
  },
  {
    spec: "anthropic:claude-3-5-haiku-20241022",
    provider: "anthropic",
    model: "claude-3-5-haiku-20241022",
    name: "Claude 3.5 Haiku",
    contextWindow: 200000,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "anthropic:claude-3-haiku-20240307",
    provider: "anthropic",
    model: "claude-3-haiku-20240307",
    name: "Claude 3 Haiku",
    contextWindow: 200000,
    speed: "fast",
    cost: "low",
  },

  // OpenAI models (verified 2025-12-26)
  {
    spec: "openai:gpt-4o",
    provider: "openai",
    model: "gpt-4o",
    name: "GPT-4o (Latest)",
    contextWindow: 128000,
    speed: "fast",
    cost: "high",
  },
  {
    spec: "openai:gpt-4o-mini",
    provider: "openai",
    model: "gpt-4o-mini",
    name: "GPT-4o Mini",
    contextWindow: 128000,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "openai:gpt-4o-2024-11-20",
    provider: "openai",
    model: "gpt-4o-2024-11-20",
    name: "GPT-4o (2024-11-20)",
    contextWindow: 128000,
    speed: "fast",
    cost: "high",
  },
  {
    spec: "openai:gpt-4o-2024-08-06",
    provider: "openai",
    model: "gpt-4o-2024-08-06",
    name: "GPT-4o (2024-08-06)",
    contextWindow: 128000,
    speed: "fast",
    cost: "high",
  },
  {
    spec: "openai:gpt-4-turbo",
    provider: "openai",
    model: "gpt-4-turbo",
    name: "GPT-4 Turbo",
    contextWindow: 128000,
    speed: "medium",
    cost: "high",
  },
  {
    spec: "openai:gpt-4-turbo-preview",
    provider: "openai",
    model: "gpt-4-turbo-preview",
    name: "GPT-4 Turbo Preview",
    contextWindow: 128000,
    speed: "medium",
    cost: "high",
  },
  {
    spec: "openai:gpt-4",
    provider: "openai",
    model: "gpt-4",
    name: "GPT-4",
    contextWindow: 8192,
    speed: "slow",
    cost: "high",
  },
  {
    spec: "openai:gpt-3.5-turbo",
    provider: "openai",
    model: "gpt-3.5-turbo",
    name: "GPT-3.5 Turbo",
    contextWindow: 16385,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "openai:gpt-3.5-turbo-0125",
    provider: "openai",
    model: "gpt-3.5-turbo-0125",
    name: "GPT-3.5 Turbo (0125)",
    contextWindow: 16385,
    speed: "fast",
    cost: "low",
  },

  // Groq models (verified 2025-01-15)
  {
    spec: "groq:llama-3.1-8b-instant",
    provider: "groq",
    model: "llama-3.1-8b-instant",
    name: "Llama 3.1 8B Instant",
    contextWindow: 131072,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "groq:llama-3.3-70b-versatile",
    provider: "groq",
    model: "llama-3.3-70b-versatile",
    name: "Llama 3.3 70B Versatile",
    contextWindow: 131072,
    speed: "medium",
    cost: "medium",
  },
  {
    spec: "groq:meta-llama/llama-guard-4-12b",
    provider: "groq",
    model: "meta-llama/llama-guard-4-12b",
    name: "Llama Guard 4 12B",
    contextWindow: 131072,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "groq:openai/gpt-oss-120b",
    provider: "groq",
    model: "openai/gpt-oss-120b",
    name: "GPT-OSS 120B",
    contextWindow: 131072,
    speed: "medium",
    cost: "low",
  },
  {
    spec: "groq:openai/gpt-oss-20b",
    provider: "groq",
    model: "openai/gpt-oss-20b",
    name: "GPT-OSS 20B",
    contextWindow: 131072,
    speed: "fast",
    cost: "low",
  },
  {
    spec: "groq:groq/compound",
    provider: "groq",
    model: "groq/compound",
    name: "Groq Compound (Web Search + Code)",
    contextWindow: 131072,
    speed: "medium",
    cost: "medium",
  },
  {
    spec: "groq:groq/compound-mini",
    provider: "groq",
    model: "groq/compound-mini",
    name: "Groq Compound Mini (Web Search + Code)",
    contextWindow: 131072,
    speed: "medium",
    cost: "low",
  },
];
